import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { db } from '../services/firebase'
import { collection, query, orderBy, getDocs } from 'firebase/firestore'
import { motion } from 'framer-motion'
import MatchCard from '../components/common/MatchCard'
import SearchBar from '../components/common/SearchBar'
import { getCurrentPosition, calculateDistance, sortMatchesByDistanceAndTime, getDerivedMatchStatus } from '../utils/helpers'
import { CalendarIcon, StarIcon, MapPinIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

const Matches = () => {
  const { t } = useTranslation()
  const { user, userData } = useAuth()
  const [searchParams] = useSearchParams()
  const [matches, setMatches] = useState([])
  const [myMatches, setMyMatches] = useState([])
  const [filteredMyMatches, setFilteredMyMatches] = useState([])
  const [filteredMatches, setFilteredMatches] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [sportFilter, setSportFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '')
  const [nearMeActive, setNearMeActive] = useState(false)
  const [userLocation, setUserLocation] = useState(null)
  const [locationLoading, setLocationLoading] = useState(false)

  useEffect(() => {
    fetchMatches()
  }, [user])

  useEffect(() => {
    filterMatches()
  }, [matches, filter, sportFilter, searchTerm, nearMeActive, userLocation])

  useEffect(() => {
    if (nearMeActive && userLocation) {
      setFilteredMatches(prev => sortByDistance(prev))
      setFilteredMyMatches(prev => sortByDistance(prev))
    }
  }, [nearMeActive, userLocation])

  const handleNearMe = async () => {
    if (nearMeActive) {
      setNearMeActive(false)
      setUserLocation(null)
      return
    }

    setLocationLoading(true)
    console.log('Matches: Starting geolocation request...')
    try {
      const position = await getCurrentPosition()
      console.log('Matches: Got position:', position)
      setUserLocation(position)
      setNearMeActive(true)
    } catch (error) {
      console.error('Matches: Error getting location:', error.code, error.message)
      if (error.code === 1 || error.message?.includes('denied')) {
        toast.error(t('location.permissionDenied') || t('location.errorGettingLocation'))
      } else {
        toast.error(t('location.errorGettingLocation'))
      }
    } finally {
      setLocationLoading(false)
    }
  }

  const getMatchDistance = (match) => {
    if (!userLocation || !match.latitude || !match.longitude) return null
    return calculateDistance(
      userLocation.latitude,
      userLocation.longitude,
      match.latitude,
      match.longitude
    )
  }

  const sortByDistance = (matchesList) => {
    if (!nearMeActive || !userLocation) return matchesList
    
    const matchesWithCoords = matchesList.filter(m => m.latitude && m.longitude)
    
    if (matchesWithCoords.length === 0) {
      toast.error(t('matches.noMatchesNearby'))
      setNearMeActive(false)
      setUserLocation(null)
      return matchesList
    }
    
    return sortMatchesByDistanceAndTime(matchesWithCoords, userLocation)
  }

  const fetchMatches = async () => {
    try {
      const matchesRef = collection(db, 'matches')
      // Order by date descending so upcoming matches appear first, completed/cancelled last
      const q = query(matchesRef, orderBy('date', 'desc'))
      const querySnapshot = await getDocs(q)
      const matchesData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      setMatches(matchesData)
      setFilteredMatches(matchesData)

      if (user) {
        const userMatches = matchesData.filter(match => {
          if (match.status === 'cancelled') return false
          const isOrganizer = match.organizerId === user.uid
          const isParticipant = match.participants?.some(
            p => p.userId === user.uid && (p.status === 'accepted' || p.status === 'pending')
          )
          return isOrganizer || isParticipant
        })
        setMyMatches(userMatches)
      }
    } catch (error) {
      console.error('Error fetching matches:', error)
    } finally {
      setLoading(false)
    }
  }

  const filterMatches = () => {
    let filtered = [...matches]
    let myFiltered = [...myMatches]

    // Always hide cancelled matches whose date has passed
    const now = new Date()
    const hidePastCancelled = (match) => {
      if (match.status === 'cancelled' && new Date(match.date) < now) {
        return false
      }
      return true
    }
    filtered = filtered.filter(hidePastCancelled)
    myFiltered = myFiltered.filter(hidePastCancelled)

    // Filter by derived status
    if (filter === 'upcoming') {
      const upcomingFilter = (match) => getDerivedMatchStatus(match.date, match.status) === 'upcoming'
      filtered = filtered.filter(upcomingFilter)
      myFiltered = myFiltered.filter(upcomingFilter)
    } else if (filter === 'ongoing') {
      const ongoingFilter = (match) => getDerivedMatchStatus(match.date, match.status) === 'ongoing'
      filtered = filtered.filter(ongoingFilter)
      myFiltered = myFiltered.filter(ongoingFilter)
    } else if (filter === 'past') {
      const pastFilter = (match) => getDerivedMatchStatus(match.date, match.status) === 'completed'
      filtered = filtered.filter(pastFilter)
      myFiltered = myFiltered.filter(pastFilter)
    }

    // Filter by sport
    if (sportFilter !== 'all') {
      const sportFilterFn = (match) => match.sport === sportFilter
      filtered = filtered.filter(sportFilterFn)
      myFiltered = myFiltered.filter(sportFilterFn)
    }

    // Filter by search term
    if (searchTerm) {
      const searchFilter = (match) =>
        match.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        match.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        match.organizerName?.toLowerCase().includes(searchTerm.toLowerCase())
      filtered = filtered.filter(searchFilter)
      myFiltered = myFiltered.filter(searchFilter)
    }

    setFilteredMatches(filtered)
    setFilteredMyMatches(myFiltered)

    // Sort: same city first, then by date (nearest first)
    const userCity = userData?.city?.trim().toLowerCase() || ''
    const userLat = Number(userData?.latitude)
    const userLon = Number(userData?.longitude)
    const userHasCoords = Number.isFinite(userLat) && Number.isFinite(userLon)
    
    const sortMatches = (a, b) => {
      if (userCity) {
        const aCity = String(a.city || '').trim().toLowerCase()
        const bCity = String(b.city || '').trim().toLowerCase()
        const aSameCity = aCity === userCity
        const bSameCity = bCity === userCity
        if (aSameCity && !bSameCity) return -1
        if (!aSameCity && bSameCity) return 1
      }
      // Then sort by distance
      if (userHasCoords) {
        const aLat = Number(a.latitude), aLon = Number(a.longitude)
        const bLat = Number(b.latitude), bLon = Number(b.longitude)
        const aHasCoords = Number.isFinite(aLat) && Number.isFinite(aLon)
        const bHasCoords = Number.isFinite(bLat) && Number.isFinite(bLon)
        if (aHasCoords && !bHasCoords) return -1
        if (!aHasCoords && bHasCoords) return 1
        if (aHasCoords && bHasCoords) {
          const distA = calculateDistance(userLat, userLon, aLat, aLon)
          const distB = calculateDistance(userLat, userLon, bLat, bLon)
          return distA - distB
        }
      }
      return 0
    }
    setFilteredMatches(prev => [...prev].sort(sortMatches))
    setFilteredMyMatches(prev => [...prev].sort(sortMatches))
  }

  const hasActiveFilters =
    filter !== 'all' ||
    sportFilter !== 'all' ||
    searchTerm.trim().length > 0 ||
    nearMeActive

  const resetFilters = () => {
    setFilter('all')
    setSportFilter('all')
    setSearchTerm('')
    setNearMeActive(false)
    setUserLocation(null)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('nav.matches')}</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">{t('matches.discoverAndJoin')}</p>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <div className="space-y-4">
          <SearchBar 
            onSearch={setSearchTerm}
            placeholder={t('matches.searchPlaceholder')}
            initialValue={searchTerm}
          />
          
          <div className="flex flex-wrap gap-3">
            {/* Mobile Quick Filters */}
            <div className="sm:hidden w-full space-y-2">
              <div className="overflow-x-auto">
                <div className="flex flex-nowrap gap-2 min-w-max">
                  <button
                    onClick={() => setFilter('all')}
                    className={`px-3 py-2 rounded-lg text-xs whitespace-nowrap transition shrink-0 ${
                      filter === 'all'
                        ? 'bg-sport-primary text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200'
                    }`}
                  >
                    {t('matches.all')}
                  </button>
                  <button
                    onClick={() => setFilter('upcoming')}
                    className={`px-3 py-2 rounded-lg text-xs whitespace-nowrap transition shrink-0 ${
                      filter === 'upcoming'
                        ? 'bg-sport-primary text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200'
                    }`}
                  >
                    {t('matches.upcoming')}
                  </button>
                  <button
                    onClick={() => setFilter('past')}
                    className={`px-3 py-2 rounded-lg text-xs whitespace-nowrap transition shrink-0 ${
                      filter === 'past'
                        ? 'bg-sport-primary text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200'
                    }`}
                  >
                    {t('matches.past')}
                  </button>
                  <button
                    onClick={() => setFilter('ongoing')}
                    className={`px-3 py-2 rounded-lg text-xs whitespace-nowrap transition shrink-0 ${
                      filter === 'ongoing'
                        ? 'bg-sport-primary text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200'
                    }`}
                  >
                    {t('matches.inProgress')}
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <div className="flex flex-nowrap gap-2 min-w-max">
                  <button
                    onClick={() => setSportFilter('calcio')}
                    className={`px-3 py-2 rounded-lg text-xs whitespace-nowrap transition shrink-0 ${
                      sportFilter === 'calcio'
                        ? 'bg-sport-primary text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200'
                    }`}
                  >
                    ⚽ {t('sports.calcio')}
                  </button>
                  <button
                    onClick={() => setSportFilter('padel')}
                    className={`px-3 py-2 rounded-lg text-xs whitespace-nowrap transition shrink-0 ${
                      sportFilter === 'padel'
                        ? 'bg-sport-primary text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200'
                    }`}
                  >
                    🎾 {t('sports.padel')}
                  </button>
                  <button
                    onClick={handleNearMe}
                    disabled={locationLoading}
                    className={`px-3 py-2 rounded-lg text-xs whitespace-nowrap transition shrink-0 ${
                      nearMeActive
                        ? 'bg-sport-secondary text-white'
                        : 'bg-sport-primary/20 text-sport-primary hover:bg-sport-primary/30'
                    } disabled:opacity-50`}
                  >
                    <MapPinIcon className="h-4 w-4 inline" />
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between gap-2">
                <button
                  onClick={resetFilters}
                  disabled={!hasActiveFilters}
                  className="px-3 py-2 rounded-lg text-xs bg-sport-primary/15 text-sport-primary hover:bg-sport-primary/25 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('matches.resetFilters')}
                </button>
              </div>
            </div>

            {/* Date Filters */}
            <div className="hidden sm:flex gap-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-lg transition ${
                  filter === 'all' 
                    ? 'bg-sport-primary text-white' 
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200'
                }`}
              >
                {t('matches.all')}
              </button>
              <button
                onClick={() => setFilter('upcoming')}
                className={`px-4 py-2 rounded-lg transition ${
                  filter === 'upcoming' 
                    ? 'bg-sport-primary text-white' 
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200'
                }`}
              >
                {t('matches.upcoming')}
              </button>
              <button
                onClick={() => setFilter('past')}
                className={`px-4 py-2 rounded-lg transition ${
                  filter === 'past' 
                    ? 'bg-sport-primary text-white' 
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200'
                }`}
              >
                {t('matches.past')}
              </button>
              <button
                onClick={() => setFilter('ongoing')}
                className={`px-4 py-2 rounded-lg transition ${
                  filter === 'ongoing' 
                    ? 'bg-sport-primary text-white' 
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200'
                }`}
              >
                {t('matches.inProgress')}
              </button>
            </div>

            {/* Sport Filters */}
            <div className="hidden sm:flex gap-2">
              <button
                onClick={() => setSportFilter('calcio')}
                className={`px-4 py-2 rounded-lg transition ${
                  sportFilter === 'calcio' 
                    ? 'bg-sport-primary text-white' 
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200'
                }`}
              >
                ⚽ {t('sports.calcio')}
              </button>
              <button
                onClick={() => setSportFilter('padel')}
                className={`px-4 py-2 rounded-lg transition ${
                  sportFilter === 'padel' 
                    ? 'bg-sport-primary text-white' 
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200'
                }`}
              >
                🎾 {t('sports.padel')}
              </button>
            </div>

            {/* Near Me Button */}
            <button
              onClick={handleNearMe}
              disabled={locationLoading}
              className={`hidden sm:flex items-center gap-1.5 px-4 py-2 rounded-lg transition ${
                nearMeActive
                  ? 'bg-sport-secondary text-white'
                  : 'bg-sport-primary/20 text-sport-primary hover:bg-sport-primary/30'
              } disabled:opacity-50`}
            >
              {locationLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <MapPinIcon className="h-5 w-5" />
              )}
              <span>{t('matches.nearMe')}</span>
            </button>

            <button
              onClick={resetFilters}
              disabled={!hasActiveFilters}
              className="hidden sm:inline-flex items-center px-4 py-2 rounded-lg bg-sport-primary/15 text-sport-primary hover:bg-sport-primary/25 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('matches.resetFilters')}
            </button>
          </div>
        </div>
      </div>

      {/* My Matches Section */}
      {!loading && filteredMyMatches.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          <div className="flex items-center gap-2">
            <StarIcon className="h-5 w-5 text-sport-primary" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">{t('matches.myMatches')}</h2>
            <span className="text-sm text-gray-500 dark:text-gray-400">({filteredMyMatches.length})</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMyMatches.map((match, index) => {
              const distance = nearMeActive && userLocation && match.latitude && match.longitude
                ? calculateDistance(userLocation.latitude, userLocation.longitude, match.latitude, match.longitude)
                : undefined
              return (
                <MatchCard key={match.id} match={match} index={index} distance={distance} />
              )
            })}
          </div>
        </motion.div>
      )}

      {/* Results Count */}
      <div className="flex justify-between items-center">
        <p className="text-gray-600 dark:text-gray-400">
          <span className="font-semibold text-gray-900 dark:text-white">Available Matches</span>
          <span className="ml-2 text-gray-600 dark:text-gray-400">({filteredMatches.length})</span>
        </p>
      </div>

      {/* Matches Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-sport-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      ) : filteredMatches.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMatches.map((match, index) => {
            const distance = nearMeActive && userLocation && match.latitude && match.longitude
              ? calculateDistance(userLocation.latitude, userLocation.longitude, match.latitude, match.longitude)
              : undefined
            return (
              <MatchCard key={match.id} match={match} index={index} distance={distance} />
            )
          })}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-12 text-center"
        >
          <CalendarIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t('matches.noMatchesFound')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {t('matches.matchingFilters')}
          </p>
        </motion.div>
      )}
    </div>
  )
}

export default Matches